import React, { useState, useEffect } from 'react';

interface Lead {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  company?: string;
  property_id?: string;
  property_title: string;
  property_location?: string;
  property_price?: string;
  request_type: 'sale' | 'rent';
  preferred_contact: string;
  preferred_day: string;
  preferred_time: string;
  request_visit?: boolean;
  visit_date?: string;
  visit_time?: string;
  additional_message?: string;
  status: string;
  priority?: string;
  notes?: string;
  assigned_to?: string;
  last_contact?: string;
  next_followup?: string;
  created_at: string;
  updated_at?: string;
  interaction_type?: string;
  lead_score?: number;
  lead_temperature?: string;
  total_interactions?: number;
  properties_viewed?: string[];
  pdf_downloads?: number;
  form_submissions?: number;
  source?: string;
}

const LeadsManagement = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [temperatureFilter, setTemperatureFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showNotes, setShowNotes] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [newNote, setNewNote] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [nextActionDate, setNextActionDate] = useState('');
  const [nextActionTime, setNextActionTime] = useState('');
  const [editingStatus, setEditingStatus] = useState('');
  const [editingPriority, setEditingPriority] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    fetchLeads();
  }, []);

  // Inicializar estados de edición cuando se selecciona un lead
  useEffect(() => {
    if (selectedLead) {
      setEditingStatus(selectedLead.status || 'nuevo');
      setEditingPriority(selectedLead.priority || 'media');
    }
  }, [selectedLead]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/property-leads`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Leads data received:', data);
        const mappedLeads = data.map((lead: any) => ({
          ...lead,
          status: lead.status || 'nuevo',
          priority: lead.priority || 'media',
          notes: lead.notes || '',
          assigned_to: lead.assigned_to || '',
          last_contact: lead.last_contact || '',
          next_followup: lead.next_followup || '',
          lead_score: lead.lead_score || 0,
          lead_temperature: lead.lead_temperature || 'cold',
          total_interactions: lead.total_interactions || 0,
          pdf_downloads: lead.pdf_downloads || 0,
          form_submissions: lead.form_submissions || 0
        }));
        setLeads(mappedLeads);
      } else {
        console.error('Error fetching leads:', response.statusText);
        alert('Error al cargar los leads');
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      alert('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const total = leads.length;
    const nuevos = leads.filter(lead => lead.status === 'nuevo').length;
    const enProceso = leads.filter(lead => ['contactado', 'en_proceso'].includes(lead.status)).length;
    const cerrados = leads.filter(lead => lead.status === 'cerrado').length;
    const conversion = total > 0 ? Math.round((cerrados / total) * 100) : 0;
    
    // Estadísticas por temperatura
    const hot = leads.filter(lead => lead.lead_temperature === 'hot').length;
    const warm = leads.filter(lead => lead.lead_temperature === 'warm').length;
    const cold = leads.filter(lead => lead.lead_temperature === 'cold').length;

    return { total, nuevos, enProceso, cerrados, conversion, hot, warm, cold };
  };

  const stats = calculateStats();

  const updateLeadStatus = async (leadId: string, newStatus: string) => {
    try {
      console.log('Updating lead status:', leadId, 'to:', newStatus);
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/property-leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          status: newStatus,
          last_contact: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
      });

      const responseText = await response.text();
      console.log('Update response:', responseText);

      if (response.ok) {
        await fetchLeads();
        alert('Estado actualizado exitosamente');
      } else {
        console.error('Error response:', responseText);
        throw new Error(`Error al actualizar estado: ${responseText}`);
      }
    } catch (error) {
      console.error('Error updating lead status:', error);
      alert(`Error al actualizar estado: ${error.message}`);
    }
  };

  const updateLeadPriority = async (leadId: string, newPriority: string) => {
    try {
      console.log('Updating lead priority:', leadId, 'to:', newPriority);
      
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/property-leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          priority: newPriority,
          updated_at: new Date().toISOString()
        })
      });

      const responseText = await response.text();
      console.log('Priority update response:', responseText);

      if (response.ok) {
        await fetchLeads();
        alert('Prioridad actualizada exitosamente');
      } else {
        console.error('Error response:', responseText);
        throw new Error(`Error al actualizar prioridad: ${responseText}`);
      }
    } catch (error) {
      console.error('Error updating lead priority:', error);
      alert(`Error al actualizar prioridad: ${error.message}`);
    }
  };

  const addNote = async (leadId: string, note: string) => {
    try {
      const lead = leads.find(l => l.id === leadId);
      if (!lead) {
        alert('Lead no encontrado');
        return;
      }

      const timestamp = new Date().toLocaleString('es-MX');
      const newNotes = lead.notes ? `${lead.notes}\n\n[${timestamp}] ${note}` : `[${timestamp}] ${note}`;

      console.log('Adding note to lead:', leadId, 'Note:', note);

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/property-leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          notes: newNotes,
          updated_at: new Date().toISOString()
        })
      });

      const responseText = await response.text();
      console.log('Note add response:', responseText);

      if (response.ok) {
        await fetchLeads();
        setNewNote('');
        setShowNotes(false);
        alert('Nota agregada exitosamente');
      } else {
        console.error('Error response:', responseText);
        throw new Error(`Error al agregar nota: ${responseText}`);
      }
    } catch (error) {
      console.error('Error adding note:', error);
      alert(`Error al agregar nota: ${error.message}`);
    }
  };

  const addNoteWithActions = async () => {
    if (!selectedLead) {
      alert('No hay lead seleccionado');
      return;
    }

    if (!newNote.trim()) {
      alert('Por favor ingresa una nota antes de guardar');
      return;
    }

    try {
      const lead = selectedLead;
      const timestamp = new Date().toLocaleString('es-MX');
      
      // Construir la nota completa con toda la información
      let fullNote = `[${timestamp}] ${newNote}`;
      
      // Agregar información de próxima acción si existe
      if (nextAction && nextActionDate) {
        fullNote += `\n  📋 Próxima acción: ${nextAction}`;
        fullNote += `\n  📅 Fecha programada: ${nextActionDate}`;
        if (nextActionTime) {
          fullNote += ` a las ${nextActionTime}`;
        }
      }
      
      // Agregar información de cambios de estado/prioridad
      if (editingStatus && editingStatus !== lead.status) {
        fullNote += `\n  📊 Estado actualizado: ${lead.status} → ${editingStatus}`;
      }
      
      if (editingPriority && editingPriority !== (lead.priority || 'media')) {
        fullNote += `\n  🎯 Prioridad actualizada: ${lead.priority || 'media'} → ${editingPriority}`;
      }

      const newNotes = lead.notes ? `${lead.notes}\n\n${fullNote}` : fullNote;

      // Preparar datos para actualizar
      const updateData: any = {
        notes: newNotes,
        updated_at: new Date().toISOString()
      };

      // Agregar cambios de estado si hay
      if (editingStatus && editingStatus !== lead.status) {
        updateData.status = editingStatus;
        updateData.last_contact = new Date().toISOString();
      }

      // Agregar cambios de prioridad si hay
      if (editingPriority && editingPriority !== (lead.priority || 'media')) {
        updateData.priority = editingPriority;
      }

      console.log('=== GUARDANDO NOTA CON ACCIONES ===');
      console.log('Lead ID:', lead.id);
      console.log('Datos a actualizar:', updateData);

      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/property-leads/${lead.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
        },
        body: JSON.stringify(updateData)
      });

      console.log('Response status:', response.status);
      const responseText = await response.text();
      console.log('Response text:', responseText);

      if (!response.ok) {
        console.error('Error response:', responseText);
        throw new Error(`Error al actualizar: ${responseText}`);
      }

      // Parsear respuesta
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        console.error('Error al parsear respuesta:', e);
        throw new Error('Respuesta inválida del servidor');
      }

      if (!result.success) {
        throw new Error(result.error || 'Error desconocido al actualizar');
      }

      console.log('✅ Lead actualizado exitosamente');

      // Recargar leads
      await fetchLeads();
      
      // Limpiar formulario
      setNewNote('');
      setNextAction('');
      setNextActionDate('');
      setNextActionTime('');
      setShowNotes(false);
      setSelectedLead(null);
      
      alert('Información actualizada exitosamente');
    } catch (error: any) {
      console.error('❌ Error updating lead with actions:', error);
      alert(`Error al actualizar: ${error.message || 'Error desconocido'}`);
    }
  };

  const createGoogleCalendarEvent = () => {
    if (!selectedLead || !nextAction || !nextActionDate) return;

    const startDate = new Date(`${nextActionDate}T${nextActionTime || '09:00'}`);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hora después

    const formatDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const title = encodeURIComponent(`Seguimiento: ${selectedLead.full_name} - ${nextAction}`);
    const details = encodeURIComponent(
      `SEGUIMIENTO DE LEAD\n\n` +
      `Cliente: ${selectedLead.full_name}\n` +
      `Email: ${selectedLead.email}\n` +
      `Teléfono: ${selectedLead.phone}\n` +
      `Propiedad: ${selectedLead.property_title}\n` +
      `Precio: ${formatPrice(selectedLead.property_price)}\n` +
      `Estado: ${selectedLead.status}\n` +
      `Prioridad: ${selectedLead.priority || 'media'}\n\n` +
      `Acción a realizar: ${nextAction}\n\n` +
      `Notas adicionales:\n${selectedLead.notes || 'Sin notas previas'}`
    );

    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${details}&reminder=15`;
    
    window.open(googleUrl, '_blank');
  };

  const createOutlookCalendarEvent = () => {
    if (!selectedLead || !nextAction || !nextActionDate) return;

    const startDate = new Date(`${nextActionDate}T${nextActionTime || '09:00'}`);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

    const title = encodeURIComponent(`Seguimiento: ${selectedLead.full_name} - ${nextAction}`);
    const body = encodeURIComponent(
      `SEGUIMIENTO DE LEAD\n\n` +
      `Cliente: ${selectedLead.full_name}\n` +
      `Email: ${selectedLead.email}\n` +
      `Teléfono: ${selectedLead.phone}\n` +
      `Propiedad: ${selectedLead.property_title}\n` +
      `Precio: ${formatPrice(selectedLead.property_price)}\n` +
      `Estado: ${selectedLead.status}\n` +
      `Prioridad: ${selectedLead.priority || 'media'}\n\n` +
      `Acción a realizar: ${nextAction}\n\n` +
      `Notas adicionales:\n${selectedLead.notes || 'Sin notas previas'}`
    );

    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${title}&body=${body}&startdt=${startDate.toISOString()}&enddt=${endDate.toISOString()}`;
    
    window.open(outlookUrl, '_blank');
  };

  const downloadICSFile = () => {
    if (!selectedLead || !nextAction || !nextActionDate) return;

    const startDate = new Date(`${nextActionDate}T${nextActionTime || '09:00'}`);
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

    const formatICSDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//CRM//Lead Management//ES',
      'BEGIN:VEVENT',
      `UID:${Date.now()}@crm-leads.com`,
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      `SUMMARY:Seguimiento: ${selectedLead.full_name} - ${nextAction}`,
      `DESCRIPTION:SEGUIMIENTO DE LEAD\n\n` +
      `Cliente: ${selectedLead.full_name}\n` +
      `Email: ${selectedLead.email}\n` +
      `Teléfono: ${selectedLead.phone}\n` +
      `Propiedad: ${selectedLead.property_title}\n` +
      `Precio: ${formatPrice(selectedLead.property_price)}\n` +
      `Estado: ${selectedLead.status}\n` +
      `Prioridad: ${selectedLead.priority || 'media'}\n\n` +
      `Acción a realizar: ${nextAction}\n\n` +
      `Notas adicionales:\n${selectedLead.notes || 'Sin notas previas'}`,
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Recordatorio de seguimiento',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `seguimiento-${selectedLead.full_name.replace(/\s+/g, '-')}-${nextActionDate}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      nuevo: 'bg-blue-100 text-blue-800',
      contactado: 'bg-yellow-100 text-yellow-800',
      en_proceso: 'bg-orange-100 text-orange-800',
      cerrado: 'bg-green-100 text-green-800',
      perdido: 'bg-red-100 text-red-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      baja: 'bg-gray-100 text-gray-800',
      media: 'bg-blue-100 text-blue-800',
      alta: 'bg-orange-100 text-orange-800',
      urgente: 'bg-red-100 text-red-800'
    };
    return colors[priority as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getTemperatureColor = (temperature: string) => {
    const colors = {
      hot: 'bg-red-100 text-red-800',
      warm: 'bg-orange-100 text-orange-800',
      cold: 'bg-blue-100 text-blue-800'
    };
    return colors[temperature as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getUrgencyIndicator = (lead: Lead) => {
    const createdDate = new Date(lead.created_at);
    const daysSinceCreated = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (lead.status === 'nuevo' && daysSinceCreated > 2) {
      return { color: 'text-red-500 animate-pulse', icon: 'ri-alarm-warning-fill', message: 'Lead sin atender por más de 2 días' };
    }
    
    if (lead.status === 'contactado' && daysSinceCreated > 5) {
      return { color: 'text-orange-500 animate-pulse', icon: 'ri-time-fill', message: 'Requiere seguimiento urgente' };
    }
    
    if (lead.request_visit && lead.status !== 'en_proceso') {
      return { color: 'text-purple-500 animate-pulse', icon: 'ri-calendar-line', message: 'Visita solicitada pero no agendada' };
    }
    
    return null;
  };

  const isNewLead = (lead: Lead) => {
    const createdDate = new Date(lead.created_at);
    const hoursSinceCreated = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60));
    return hoursSinceCreated <= 24; // Nuevo si tiene menos de 24 horas
  };

  const hasRecentInteraction = (lead: Lead) => {
    if (!lead.updated_at || !lead.created_at) return false;
    const createdDate = new Date(lead.created_at);
    const updatedDate = new Date(lead.updated_at);
    const timeDiff = updatedDate.getTime() - createdDate.getTime();
    const hoursDiff = Math.floor(timeDiff / (1000 * 60 * 60));
    
    // Si la última actualización fue en las últimas 24 horas Y es diferente a la creación
    return hoursDiff > 0 && hoursDiff <= 24;
  };

  const getInteractionBadge = (lead: Lead) => {
    const interactions = lead.total_interactions || 0;
    
    if (interactions === 0 || interactions === 1) {
      return null; // No mostrar badge para primera interacción
    }
    
    if (interactions >= 5) {
      return {
        text: `${interactions} interacciones`,
        color: 'bg-red-100 text-red-800 border-2 border-red-300',
        icon: 'ri-fire-fill',
        pulse: true
      };
    } else if (interactions >= 3) {
      return {
        text: `${interactions} interacciones`,
        color: 'bg-orange-100 text-orange-800 border-2 border-orange-300',
        icon: 'ri-notification-badge-fill',
        pulse: true
      };
    } else {
      return {
        text: `${interactions} interacciones`,
        color: 'bg-blue-100 text-blue-800 border border-blue-300',
        icon: 'ri-repeat-fill',
        pulse: false
      };
    }
  };

  const formatPrice = (price: string | undefined) => {
    if (!price) return 'No especificado';
    const numPrice = parseFloat(price);
    return isNaN(numPrice) ? price : `$${numPrice.toLocaleString()} MXN`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Cargando leads...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gestión de Leads</h2>
        <button
          onClick={fetchLeads}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>
          Actualizar
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <i className="ri-user-add-line text-xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Leads</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <i className="ri-time-line text-xl text-yellow-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">En Proceso</p>
              <p className="text-2xl font-bold text-gray-900">{stats.enProceso}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <i className="ri-check-double-line text-xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Cerrados</p>
              <p className="text-2xl font-bold text-gray-900">{stats.cerrados}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <i className="ri-percent-line text-xl text-purple-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Conversión</p>
              <p className="text-2xl font-bold text-gray-900">{stats.conversion}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Temperature Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <i className="ri-fire-line text-lg text-red-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Leads Calientes</p>
              <p className="text-xl font-bold text-red-600">{stats.hot}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <i className="ri-sun-line text-lg text-orange-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Leads Tibios</p>
              <p className="text-xl font-bold text-orange-600">{stats.warm}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <i className="ri-snowflake-line text-lg text-blue-600"></i>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Leads Fríos</p>
              <p className="text-xl font-bold text-blue-600">{stats.cold}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nombre, email o propiedad..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos los estados</option>
              <option value="nuevo">Nuevo</option>
              <option value="contactado">Contactado</option>
              <option value="en_proceso">En Proceso</option>
              <option value="cerrado">Cerrado</option>
              <option value="perdido">Perdido</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todas las prioridades</option>
              <option value="baja">Baja</option>
              <option value="media">Media</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Temperatura</label>
            <select
              value={temperatureFilter}
              onChange={(e) => setTemperatureFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todas las temperaturas</option>
              <option value="hot">🔥 Caliente</option>
              <option value="warm">☀️ Tibio</option>
              <option value="cold">❄️ Frío</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setPriorityFilter('all');
                setTemperatureFilter('all');
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line mr-2"></i>
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Propiedad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Interacciones
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prioridad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Temperatura
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contacto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(() => {
                const filteredLeads = leads.filter(lead => {
                  const matchesSearch = searchTerm === '' || 
                    lead.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    lead.property_title.toLowerCase().includes(searchTerm.toLowerCase());
                  
                  const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
                  const matchesPriority = priorityFilter === 'all' || lead.priority === priorityFilter;
                  const matchesTemperature = temperatureFilter === 'all' || lead.lead_temperature === temperatureFilter;
                  
                  return matchesSearch && matchesStatus && matchesPriority && matchesTemperature;
                });

                const paginatedLeads = filteredLeads.slice(
                  (currentPage - 1) * itemsPerPage,
                  currentPage * itemsPerPage
                );

                const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);

                return paginatedLeads.map((lead) => {
                  const urgencyIndicator = getUrgencyIndicator(lead);
                  const isNew = isNewLead(lead);
                  const hasNewInteraction = hasRecentInteraction(lead);
                  const interactionBadge = getInteractionBadge(lead);
                  
                  return (
                    <tr 
                      key={lead.id} 
                      className={`hover:bg-gray-50 ${isNew ? 'bg-blue-50' : ''} ${hasNewInteraction ? 'bg-green-50' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isNew ? 'bg-blue-600 animate-pulse' : 'bg-blue-100'}`}>
                              <i className={`ri-user-line ${isNew ? 'text-white' : 'text-blue-600'}`}></i>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-gray-900">{lead.full_name}</div>
                              {isNew && (
                                <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded-full animate-pulse">
                                  ¡NUEVO!
                                </span>
                              )}
                              {hasNewInteraction && !isNew && (
                                <span className="px-2 py-1 bg-green-600 text-white text-xs font-bold rounded-full animate-pulse">
                                  ¡NUEVA ACTIVIDAD!
                                </span>
                              )}
                              {urgencyIndicator && (
                                <div className={`ml-2 ${urgencyIndicator.color}`} title={urgencyIndicator.message}>
                                  <i className={urgencyIndicator.icon}></i>
                                </div>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{lead.email}</div>
                            <div className="text-sm text-gray-500">{lead.phone}</div>
                            {lead.company && (
                              <div className="text-xs text-gray-400">{lead.company}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{lead.property_title}</div>
                        <div className="text-sm text-gray-500">
                          {lead.request_type === 'sale' ? 'Compra' : 'Renta'} - {formatPrice(lead.property_price)}
                        </div>
                        {lead.property_location && (
                          <div className="text-xs text-gray-400">{lead.property_location}</div>
                        )}
                        {lead.request_visit && (
                          <div className="text-xs text-purple-600 font-medium">
                            <i className="ri-calendar-line mr-1"></i>
                            Visita solicitada
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-2">
                          {/* Badge de interacciones */}
                          {interactionBadge && (
                            <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${interactionBadge.color} ${interactionBadge.pulse ? 'animate-pulse' : ''}`}>
                              <i className={`${interactionBadge.icon} mr-1`}></i>
                              {interactionBadge.text}
                            </div>
                          )}
                          
                          {/* Detalles de interacciones */}
                          <div className="text-xs text-gray-600 space-y-1">
                            <div className="flex items-center gap-1">
                              <i className="ri-file-pdf-line text-purple-600"></i>
                              <span>{lead.pdf_downloads || 0} PDFs</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <i className="ri-file-list-line text-blue-600"></i>
                              <span>{lead.form_submissions || 0} Formularios</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <i className="ri-star-line text-yellow-600"></i>
                              <span>Score: {lead.lead_score || 0}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={lead.status}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          className={`px-2 py-1 text-xs font-semibold rounded-full border-0 ${getStatusColor(lead.status)} cursor-pointer`}
                        >
                          <option value="nuevo">Nuevo</option>
                          <option value="contactado">Contactado</option>
                          <option value="en_proceso">En Proceso</option>
                          <option value="cerrado">Cerrado</option>
                          <option value="perdido">Perdido</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={lead.priority || 'media'}
                          onChange={(e) => updateLeadPriority(lead.id, e.target.value)}
                          className={`px-2 py-1 text-xs font-semibold rounded-full border-0 ${getPriorityColor(lead.priority || 'media')} cursor-pointer`}
                        >
                          <option value="baja">Baja</option>
                          <option value="media">Media</option>
                          <option value="alta">Alta</option>
                          <option value="urgente">Urgente</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTemperatureColor(lead.lead_temperature || 'cold')}`}>
                          {lead.lead_temperature === 'hot' && '🔥 Caliente'}
                          {lead.lead_temperature === 'warm' && '☀️ Tibio'}
                          {lead.lead_temperature === 'cold' && '❄️ Frío'}
                          {!lead.lead_temperature && '❄️ Frío'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>Prefiere: {lead.preferred_contact}</div>
                        <div>Día: {lead.preferred_day}</div>
                        <div>Hora: {lead.preferred_time}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <a
                            href={`https://wa.me/${lead.phone.replace(/\D/g, '')}?text=Hola ${lead.full_name}, te contacto por tu interés en ${lead.property_title}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:text-green-900 cursor-pointer"
                            title="WhatsApp"
                          >
                            <i className="ri-whatsapp-line text-lg"></i>
                          </a>
                          <a
                            href={`mailto:${lead.email}?subject=Información sobre ${lead.property_title}&body=Hola ${lead.full_name},%0D%0A%0D%0AGracias por tu interés en ${lead.property_title}.`}
                            className="text-blue-600 hover:text-blue-900 cursor-pointer"
                            title="Email"
                          >
                            <i className="ri-mail-line text-lg"></i>
                          </a>

                          <button
                            onClick={() => {
                              setSelectedLead(lead);
                              setEditingStatus(lead.status || 'nuevo');
                              setEditingPriority(lead.priority || 'media');
                              setShowNotes(true);
                            }}
                            className="text-purple-600 hover:text-purple-900 cursor-pointer"
                            title="Ver detalles y notas"
                          >
                            <i className="ri-file-text-line text-lg"></i>
                          </button>

                        </div>
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {(() => {
          const filteredLeads = leads.filter(lead => {
            const matchesSearch = searchTerm === '' || 
              lead.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
              lead.property_title.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
            const matchesPriority = priorityFilter === 'all' || lead.priority === priorityFilter;
            const matchesTemperature = temperatureFilter === 'all' || lead.lead_temperature === temperatureFilter;
            
            return matchesSearch && matchesStatus && matchesPriority && matchesTemperature;
          });

          const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);

          if (totalPages <= 1) return null;

          return (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  Siguiente
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> a{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, filteredLeads.length)}
                    </span>{' '}
                    de <span className="font-medium">{filteredLeads.length}</span> resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium cursor-pointer ${
                          page === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Notes Modal */}
      {showNotes && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Detalles del Lead - {selectedLead.full_name}
                </h3>
                <button
                  onClick={() => {
                    setShowNotes(false);
                    setNewNote('');
                    setNextAction('');
                    setNextActionDate('');
                    setNextActionTime('');
                    setSelectedLead(null);
                    setEditingStatus('');
                    setEditingPriority('');
                  }}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Lead Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{selectedLead.lead_score || 0}</div>
                    <div className="text-sm text-blue-600">Puntuación</div>
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{selectedLead.total_interactions || 0}</div>
                    <div className="text-sm text-green-600">Interacciones</div>
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{selectedLead.pdf_downloads || 0}</div>
                    <div className="text-sm text-purple-600">PDFs Descargados</div>
                  </div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{selectedLead.form_submissions || 0}</div>
                    <div className="text-sm text-orange-600">Formularios</div>
                  </div>
                </div>
              </div>

              {/* Estado y Prioridad Editables */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-flag-line mr-2 text-blue-600"></i>
                    Estado del Lead
                  </h4>
                  <select
                    value={editingStatus}
                    onChange={(e) => setEditingStatus(e.target.value)}
                    className={`w-full px-3 py-2 text-sm font-semibold rounded-lg border-2 ${getStatusColor(editingStatus)} cursor-pointer`}
                  >
                    <option value="nuevo">🆕 Nuevo</option>
                    <option value="contactado">📞 Contactado</option>
                    <option value="en_proceso">⏳ En Proceso</option>
                    <option value="cerrado">✅ Cerrado</option>
                    <option value="perdido">❌ Perdido</option>
                  </select>
                  {editingStatus !== selectedLead.status && (
                    <p className="text-xs text-blue-600 mt-2">
                      <i className="ri-information-line mr-1"></i>
                      Se actualizará al guardar la nota
                    </p>
                  )}
                </div>

                <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg border border-orange-200">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <i className="ri-arrow-up-line mr-2 text-orange-600"></i>
                    Prioridad del Lead
                  </h4>
                  <select
                    value={editingPriority}
                    onChange={(e) => setEditingPriority(e.target.value)}
                    className={`w-full px-3 py-2 text-sm font-semibold rounded-lg border-2 ${getPriorityColor(editingPriority)} cursor-pointer`}
                  >
                    <option value="baja">⬇️ Baja</option>
                    <option value="media">➖ Media</option>
                    <option value="alta">⬆️ Alta</option>
                    <option value="urgente">🚨 Urgente</option>
                  </select>
                  {editingPriority !== selectedLead.priority && (
                    <p className="text-xs text-orange-600 mt-2">
                      <i className="ri-information-line mr-1"></i>
                      Se actualizará al guardar la nota
                    </p>
                  )}
                </div>
              </div>

              {/* Lead Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Información del Cliente</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p><strong>Nombre:</strong> {selectedLead.full_name}</p>
                    <p><strong>Email:</strong> {selectedLead.email}</p>
                    <p><strong>Teléfono:</strong> {selectedLead.phone}</p>
                    {selectedLead.company && <p><strong>Empresa:</strong> {selectedLead.company}</p>}
                    <p><strong>Fuente:</strong> {selectedLead.source || 'No especificada'}</p>
                    <p><strong>Tipo de interacción:</strong> {selectedLead.interaction_type || 'No especificada'}</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Preferencias de Contacto</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p><strong>Método:</strong> {selectedLead.preferred_contact}</p>
                    <p><strong>Día:</strong> {selectedLead.preferred_day}</p>
                    <p><strong>Horario:</strong> {selectedLead.preferred_time}</p>
                    <p><strong>Estado:</strong> <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedLead.status)}`}>{selectedLead.status}</span></p>
                    <p><strong>Prioridad:</strong> <span className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(selectedLead.priority || 'media')}`}>{selectedLead.priority || 'media'}</span></p>
                    <p><strong>Temperatura:</strong> <span className={`px-2 py-1 rounded-full text-xs ${getTemperatureColor(selectedLead.lead_temperature || 'cold')}`}>
                      {selectedLead.lead_temperature === 'hot' && '🔥 Caliente'}
                      {selectedLead.lead_temperature === 'warm' && '☀️ Tibio'}
                      {(selectedLead.lead_temperature === 'cold' || !selectedLead.lead_temperature) && '❄️ Frío'}
                    </span></p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Propiedad de Interés</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium">{selectedLead.property_title}</p>
                  <p className="text-sm text-gray-600">
                    {selectedLead.request_type === 'sale' ? 'Compra' : 'Renta'} - {formatPrice(selectedLead.property_price)}
                  </p>
                  {selectedLead.property_location && (
                    <p className="text-sm text-gray-500">📍 {selectedLead.property_location}</p>
                  )}
                  {selectedLead.request_visit && (
                    <p className="text-sm text-purple-600 font-medium mt-2">
                      <i className="ri-calendar-line mr-1"></i>
                      Visita solicitada para: {selectedLead.visit_date} a las {selectedLead.visit_time}
                    </p>
                  )}
                </div>
              </div>

              {selectedLead.additional_message && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Mensaje del Cliente</h4>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">{selectedLead.additional_message}</p>
                  </div>
                </div>
              )}

              {/* Notes Section */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Historial de Seguimiento</h4>
                {selectedLead.notes ? (
                  <div className="bg-gray-50 p-4 rounded-lg max-h-40 overflow-y-auto">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap">{selectedLead.notes}</pre>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No hay notas de seguimiento aún.</p>
                )}
              </div>

              {/* Próximas Acciones */}
              <div className="mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-lg border-2 border-purple-200">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
                  <i className="ri-calendar-check-line mr-2 text-purple-600"></i>
                  Programar Próxima Acción
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="ri-task-line mr-1"></i>
                      Descripción de la acción
                    </label>
                    <input
                      type="text"
                      value={nextAction}
                      onChange={(e) => setNextAction(e.target.value)}
                      placeholder="Ej: Llamar para agendar visita, Enviar propuesta, Seguimiento..."
                      className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <i className="ri-calendar-line mr-1"></i>
                        Fecha
                      </label>
                      <input
                        type="date"
                        value={nextActionDate}
                        onChange={(e) => setNextActionDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <i className="ri-time-line mr-1"></i>
                        Hora (opcional)
                      </label>
                      <input
                        type="time"
                        value={nextActionTime}
                        onChange={(e) => setNextActionTime(e.target.value)}
                        className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Botones de Calendario */}
                  {nextAction && nextActionDate && (
                    <div className="mt-4 p-4 bg-white rounded-lg border border-purple-200">
                      <p className="text-sm font-medium text-gray-700 mb-3">
                        <i className="ri-calendar-event-line mr-1"></i>
                        Agregar a tu calendario:
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <button
                          onClick={createGoogleCalendarEvent}
                          className="flex items-center justify-center px-4 py-2 bg-white border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-google-fill mr-2"></i>
                          Google Calendar
                        </button>
                        <button
                          onClick={createOutlookCalendarEvent}
                          className="flex items-center justify-center px-4 py-2 bg-white border-2 border-blue-700 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-microsoft-fill mr-2"></i>
                          Outlook
                        </button>
                        <button
                          onClick={downloadICSFile}
                          className="flex items-center justify-center px-4 py-2 bg-white border-2 border-purple-500 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-download-line mr-2"></i>
                          Descargar .ics
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        <i className="ri-information-line mr-1"></i>
                        El archivo .ics es compatible con Apple Calendar, Thunderbird y otros calendarios
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Add Note */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-add-line mr-2 text-green-600"></i>
                  Agregar Nota de Seguimiento
                </h4>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Escribe una nota sobre la interacción con este cliente..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                />
                <div className="flex justify-end mt-4 gap-3">
                  <button
                    onClick={() => {
                      setShowNotes(false);
                      setNewNote('');
                      setNextAction('');
                      setNextActionDate('');
                      setNextActionTime('');
                      setSelectedLead(null);
                      setEditingStatus('');
                      setEditingPriority('');
                    }}
                    className="px-6 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={addNoteWithActions}
                    disabled={!newNote.trim()}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-save-line mr-2"></i>
                    Guardar Todo
                  </button>
                </div>
                {(editingStatus !== selectedLead.status || editingPriority !== selectedLead.priority || nextAction) && (
                  <p className="text-sm text-blue-600 mt-2 text-right">
                    <i className="ri-information-line mr-1"></i>
                    Se guardarán: nota, estado, prioridad y próxima acción
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsManagement;
